import { z } from 'zod';
import {
  BooleanFilter,
  booleanFilterSchema
} from 'v5/api/_common/query/filters/BooleanFilter';
import {
  paginationSchema,
  paginationUtils
} from 'v5/api/_common/query/pagination';
import {
  withBuilderUtils,
  withFiltersUtils,
  WithOptionalFilters,
  WithPagination,
  withPaginationUtils,
  WithSorter,
  withSorterUtils
} from 'v5/api/_common/query/queryUtils';
import type { Sorter } from 'v5/api/_common/query/sorters/Sorter';
import type {
  ReportEntityType,
  ReportTemplate
} from 'v5/api/report-templates/reportTemplates.api';
import type { ValidatedRow } from 'v5/platform/row-level-validation';
import type { Nullable } from 'v5/platform/typescript';

export const REPORT_TEMPLATE_SORT_FIELDS = ['name', 'type'] as const;

export type ReportTemplateSortField =
  (typeof REPORT_TEMPLATE_SORT_FIELDS)[number];

export type ReportTemplateListQuery = WithPagination &
  WithSorter<ReportTemplateSortField> &
  WithOptionalFilters<{ archived: BooleanFilter }>;

export const reportTemplateListQueryUtils = withBuilderUtils({
  ...withFiltersUtils<ReportTemplateListQuery>(),
  ...withPaginationUtils<ReportTemplateListQuery>(),
  ...withSorterUtils<ReportTemplateListQuery>(),
  ofEmpty: (): ReportTemplateListQuery => ({
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {}
  }),
  ofDefault: (): ReportTemplateListQuery => ({
    sort: null,
    page: paginationUtils.ofDefault(),
    filters: {
      archived: { operator: 'equal', value: false }
    }
  })
}).setInitialQueryFactory(x => x.ofDefault);

/** qs turns `null` into `sort=`; treat empty / junk as null. */
const strToNull = (value: unknown) =>
  value === '' || value === '-' ? null : value;

export const reportTemplateListQuerySchema = z
  .object({
    filters: z
      .object({
        archived: z
          .preprocess(
            raw =>
              typeof raw === 'object' && raw !== null && 'value' in raw
                ? { operator: 'equal', value: raw.value }
                : raw,
            booleanFilterSchema
          )
          .optional()
      })
      .partial()
      .optional(),
    page: paginationSchema.optional(),
    sort: z
      .preprocess(
        strToNull,
        z
          .object({
            field: z.enum(REPORT_TEMPLATE_SORT_FIELDS),
            order: z.enum(['asc', 'desc'])
          })
          .nullable()
      )
      .optional()
  })
  .transform(
    (value): ReportTemplateListQuery => ({
      sort: value.sort ?? null,
      page: value.page ?? paginationUtils.ofDefault(),
      filters: {
        archived: value.filters?.archived ?? {
          operator: 'equal',
          value: false
        }
      }
    })
  )
  .catch(() => reportTemplateListQueryUtils.ofDefault());

export function isArchivedReportTemplateQuery(
  query: ReportTemplateListQuery
): boolean {
  return query.filters?.archived?.value ?? false;
}

/** An invalid row keeps its tab when its raw data still says whether it is archived. */
function isRowArchived(row: ValidatedRow<ReportTemplate>): boolean {
  if (row.isValid) {
    return row.data.archived;
  }
  const raw = row.rawItem;
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'archived' in raw &&
    raw.archived === true
  );
}

/**
 * The rows of one tab (active or archived), sorted; rows that failed parsing
 * stay in the list after the sorted ones, in the order the server sent them.
 */
export function selectReportTemplateRows(
  rows: ValidatedRow<ReportTemplate>[],
  view: {
    archived: boolean;
    sort: Nullable<Sorter<ReportTemplateSortField>>;
  },
  typeLabel: (type: ReportEntityType) => string
): ValidatedRow<ReportTemplate>[] {
  const inTab = rows.filter(row => isRowArchived(row) === view.archived);
  const valid = inTab.flatMap(row => (row.isValid ? [row] : []));
  const invalid = inTab.filter(row => !row.isValid);
  const byName = (a: ReportTemplate, b: ReportTemplate) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  const compare = (a: ReportTemplate, b: ReportTemplate) => {
    switch (view.sort?.field) {
      case 'type':
        return (
          typeLabel(a.entityType).localeCompare(
            typeLabel(b.entityType),
            undefined,
            { sensitivity: 'base' }
          ) || byName(a, b)
        );
      case 'name':
      case undefined:
        return byName(a, b);
    }
  };
  const direction = view.sort?.order === 'desc' ? -1 : 1;
  const sorted = [...valid].sort((a, b) => compare(a.data, b.data) * direction);
  return [...sorted, ...invalid];
}
